import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Timesheets → Time off tab (Kloepfer K3-63, 2026-10-19): approve or deny a
// worker's time-off request. The tab redraws in place from the server's
// answer (no page reload), the tab's count follows, and a toast says it's done.
// On the Calendar (K13-16) a bar opens the request, with Approve / Deny while
// it waits; the redraw keeps the calendar's month.
export default class extends Controller {
  static targets = ["section", "denyModal", "denyMessage", "denyNote", "denyButton",
    "detailModal", "detailName", "detailKind", "detailDates", "detailNote", "detailStatus", "detailApprove", "detailDeny"]
  static values = { year: Number, approveUrl: String, denyUrl: String, failed: String, layout: String, month: String }

  async approve(event) {
    const button = event.currentTarget
    button.disabled = true
    const ok = await this._decide(this.approveUrlValue, button.dataset.id, "")
    if (!ok) button.disabled = false
  }

  openDeny(event) {
    const button = event.currentTarget
    this.denyId = button.dataset.id
    const template = this.denyMessageTarget.dataset.template || ""
    this.denyMessageTarget.textContent = template.replace("__NAME__", button.dataset.name || "")
    this.denyNoteTarget.value = ""
    this.denyButtonTarget.disabled = false
    this.denyModalTarget.classList.remove("hidden")
    this.denyNoteTarget.focus()
  }

  async deny() {
    if (!this.denyId) return
    this.denyButtonTarget.disabled = true
    const ok = await this._decide(this.denyUrlValue, this.denyId, this.denyNoteTarget.value.trim())
    if (ok) this.hideModal()
    else this.denyButtonTarget.disabled = false
  }

  hideModal() {
    this.denyModalTarget.classList.add("hidden")
    this.denyId = null
  }

  openDetail(event) {
    const d = event.currentTarget.dataset
    const waiting = d.pending === "1"
    this.detailNameTarget.textContent = d.name || ""
    this.detailKindTarget.textContent = d.kind || ""
    this.detailDatesTarget.textContent = d.dates || ""
    this.detailNoteTarget.textContent = d.note ? `“${d.note}”` : ""
    this.detailNoteTarget.classList.toggle("hidden", !d.note)
    this.detailStatusTarget.textContent = d.status || ""
    for (const button of [this.detailApproveTarget, this.detailDenyTarget]) {
      button.classList.toggle("hidden", !waiting)
      button.disabled = false
      button.dataset.id = d.id
      button.dataset.name = d.name || ""
    }
    this.detailModalTarget.classList.remove("hidden")
    track("Time Off Request Opened", { view: "calendar", waiting })
  }

  hideDetail() {
    this.detailModalTarget.classList.add("hidden")
  }

  async approveFromDetail(event) {
    const button = event.currentTarget
    button.disabled = true
    const ok = await this._decide(this.approveUrlValue, button.dataset.id, "")
    if (ok) this.hideDetail()
    else button.disabled = false
  }

  denyFromDetail(event) {
    this.hideDetail()
    this.openDeny(event)
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async _decide(urlTemplate, id, note) {
    try {
      const res = await fetch(urlTemplate.replace("__ID__", encodeURIComponent(id)), {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ note, year: this.yearValue, layout: this.layoutValue || undefined, month: this.monthValue || undefined })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        this._showToast(data.error || this.failedValue, "error")
        return false
      }
      this.sectionTarget.innerHTML = data.html
      this._updateBadge(data.waiting)
      this._showToast(data.message, "success")
      return true
    } catch (_e) {
      this._showToast(this.failedValue, "error")
      return false
    }
  }

  // Paid time off hours for one person (K3-94): saved as typed, and the
  // row's used / waiting / left follow. Empty clears them.
  async savePto(event) {
    const input = event.currentTarget
    const row = input.closest("[data-pto-row]")
    input.disabled = true
    try {
      const res = await fetch(input.dataset.url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ user_id: input.dataset.userId, pto_hours: input.value.trim(), year: this.yearValue })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        this._showToast(data.error || this.failedValue, "error")
        return
      }
      const fmt = v => (v == null ? "—" : `${Number(v.toFixed(2))} h`)
      const pto = data.pto
      if (row) {
        row.querySelector("[data-pto-used]").textContent = pto ? fmt(pto.used) : "—"
        row.querySelector("[data-pto-waiting]").textContent = pto ? fmt(pto.waiting) : "—"
        row.querySelector("[data-pto-left]").textContent = pto ? fmt(pto.left) : "—"
      }
      this._showToast(data.message, "success")
    } catch (_e) {
      this._showToast(this.failedValue, "error")
    } finally {
      input.disabled = false
    }
  }

  // The count on the Time off tab.
  _updateBadge(count) {
    const badge = document.querySelector("[data-time-off-badge]")
    if (!badge) return
    if (count > 0) badge.textContent = count
    else badge.remove()
  }

  _showToast(message, type = "success") {
    if (!message) return
    const cssType = type === "error" ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msg)
    document.body.prepend(el)
  }
}
